import React, { useEffect, useState } from "react";
import { getRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { getDatesBetween, toJSTDate, formatDate } from "../../utils/formatDate";
import { gradeOptions, filterStudentsFromGrade } from "../../constants/gradeOptions";
import { toast } from "react-toastify";

const periods = [1, 2, 3, 4, 5, 6, 7];
const SESSION_KEY = "hideMorningPeriods";
const weekdays = ["日", "月", "火", "水", "木", "金", "土"];

const AdminStudentSchedule = () => {
    const [hideMorning, setHideMorning] = useState(() => sessionStorage.getItem(SESSION_KEY) === "true");
    const [teachers, setTeachers] = useState([]);
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [unavailTeachers, setUnavailTeachers] = useState([]);
    const [unavailStudents, setUnavailStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [scheduleMap, setScheduleMap] = useState({});
    const [dates, setDates] = useState([]);
    const [entryWindow, setEntryWindow] = useState(null);
    const [teacherSlotMap, setTeacherSlotMap] = useState({});
    const [selectedGrade, setSelectedGrade] = useState("all");
    const [regularHolidays, setRegularHolidays] = useState([]);
    const [specialHolidays, setSpecialHolidays] = useState([]);

    useEffect(() => {
        const fetchInitial = async () => {
            try {
                const [teacherRes, studentRes, subjectRes, windowRes, regRes, specRes] = await Promise.all([
                    getRequest("teachers?excludeRetired=true"),
                    getRequest("students?excludeWithdrawn=true"),
                    getRequest("subjects"),
                    getRequest("shiftEntryWindows"),
                    getRequest("schoolRegularHolidays"),
                    getRequest("schoolSpecialHolidays")
                ]);
                setTeachers(teacherRes.teachers || []);
                setStudents(studentRes.students || []);
                setSubjects(subjectRes.subjects || []);
                setRegularHolidays(regRes.holidays || []);
                setSpecialHolidays(specRes.holidays || []);

                const wins = windowRes.windows || [];
                if (wins.length > 0) {
                    wins.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
                    const latest = wins[0];
                    setEntryWindow(latest);
                    const dlist = getDatesBetween(latest.start_date, latest.end_date);
                    setDates(dlist);
                }
            } catch (err) {
                console.error("初期データ取得エラー:", err);
                toast.error("初期データ取得に失敗しました");
            }
        };
        fetchInitial();
    }, []);

    useEffect(() => {
        if (!selectedStudent || !entryWindow) return;
        const fetchRestrictions = async () => {
            try {
                const [ut, us] = await Promise.all([
                    getRequest("teacherUnavailabilities/all"),
                    getRequest("studentUnavailabilities/all")
                ]);
                setUnavailTeachers(ut.unavailabilities || []);
                setUnavailStudents(us.unavailabilities || []);
            } catch (err) {
                console.error("出欠制限取得エラー:", err);
            }
        };
        fetchRestrictions();
    }, [selectedStudent, entryWindow]);

    const handleStudentSelect = async (studentid) => {
        setSelectedStudent(studentid);
        try {
            const start = entryWindow.start_date;
            const end = entryWindow.end_date;
            const res = await getRequest(`lessonSchedules?start=${start}&end=${end}`); // studentidは付けない

            const map = {};
            const teacherSlotCount = {};

            res.schedules.forEach(item => {
                const key = `${formatDate(item.date)}_${item.period}`;
                const teacherKey = `${item.teacherid}_${key}`;

                // 講師ごとのコマ使用数をカウント
                if (item.teacherid) {
                    if (!teacherSlotCount[teacherKey]) teacherSlotCount[teacherKey] = 0;
                    if (item.student1id) teacherSlotCount[teacherKey]++;
                    if (item.student2id) teacherSlotCount[teacherKey]++;
                }

                // 生徒本人の枠だけ保存
                if (item.student1id === studentid || item.student2id === studentid) {
                    map[key] = {
                        teacherid: item.teacherid || "",
                        previousTeacherid: item.teacherid || "",
                        subjectid: item.student1id === studentid ? item.subject1id : item.subject2id,
                        student1id: item.student1id,
                        student2id: item.student2id
                    };
                }
            });

            setScheduleMap(map);
            setTeacherSlotMap(teacherSlotCount);
        } catch (err) {
            console.error("スケジュール取得エラー:", err);
            toast.error("スケジュールの取得に失敗しました");
        }
    };

    const handleSave = async () => {
        if (!selectedStudent) return;

        const saveEntries = [];
        const deleteEntries = [];
        const invalidEntries = [];

        Object.entries(scheduleMap).forEach(([key, val]) => {
            const [date, period] = key.split("_");
            const { teacherid, subjectid, previousTeacherid } = val;

            if (teacherid && subjectid) {
                // 保存対象
                saveEntries.push({
                    date,
                    period: Number(period),
                    teacherid,
                    subjectid,
                    studentid: selectedStudent,
                });

                // 講師が変更されていた場合、元の講師から削除
                if (previousTeacherid && previousTeacherid !== teacherid) {
                    deleteEntries.push({
                        date,
                        period: Number(period),
                        teacherid: previousTeacherid,
                        studentid: selectedStudent,
                    });
                }

            } else if ((teacherid || subjectid) && previousTeacherid) {
                // 不完全になった＆元データがある → 削除対象
                deleteEntries.push({
                    teacherid: previousTeacherid,
                    date,
                    period: Number(period),
                    studentid: selectedStudent,
                });

            } else if (teacherid || subjectid) {
                // 新規入力だけど不完全 → 警告
                invalidEntries.push(`${date}・${period}講`);
            }
        });

        if (invalidEntries.length > 0) {
            toast.error(`以下のコマは講師が未選択のため処理できません：\n${invalidEntries.join("、")}`);
            return;
        }

        try {
            await Promise.all(saveEntries.map(entry => putRequest("lessonSchedules/student", entry)));
            await Promise.all(deleteEntries.map(entry => deleteRequest("lessonSchedules/student", entry)));
            toast.success("保存が完了しました");
        } catch (err) {
            console.error("保存エラー:", err);
            toast.error("保存に失敗しました");
        }
    };

    const isStudentUnavailable = (studentid, date, period) => {
        return unavailStudents.some(u => studentid === u.studentid && period === u.period && formatDate(date) === toJSTDate(u.date).toISOString().split("T")[0]);
    };

    const isTeacherUnavailable = (teacherid, date, period) => {
        return unavailTeachers.some(u => teacherid === u.teacherid && period === u.period && formatDate(date) === toJSTDate(u.date).toISOString().split("T")[0]);
    };

    const isHoliday = (date, period) => {
        const dow = new Date(date).getDay();
        const isRegular = regularHolidays.some(h => h.day_of_week === dow && (h.period === null || h.period === period));
        const isSpecial = specialHolidays.some(h => {
            const holidayStr = toJSTDate(h.date).toISOString().split("T")[0];
            return holidayStr === date && (h.period === null || h.period === period);
        });
        return isRegular || isSpecial;
    };

    const renderWeeklyCalendar = () => {
        const visiblePeriods = hideMorning ? periods.filter(p => p > 3) : periods;
        const byWeeks = [];
        let week = new Array(7).fill(null);
        dates.forEach(date => {
            const d = new Date(toJSTDate(date));
            const dow = d.getDay();
            if (week.every(v => v === null) && dow !== 0) {
                for (let i = 0; i < dow; i++) week[i] = "out";
            }
            week[dow] = date;
            if (dow === 6) {
                byWeeks.push(week);
                week = new Array(7).fill(null);
            }
        });
        if (week.some(d => d)) byWeeks.push(week);

        return (
            <div className="space-y-10">
                <div className="mb-4 text-sm">
                    <label>
                        <input
                            type="checkbox"
                            className="mr-2"
                            checked={hideMorning}
                            onChange={(e) => {
                                setHideMorning(e.target.checked);
                                sessionStorage.setItem(SESSION_KEY, e.target.checked);
                            }}
                        />
                        1～3講を非表示
                    </label>
                </div>
                {byWeeks.map((week, wi) => (
                    <div key={wi}>
                        <div className="grid grid-cols-7 text-center text-sm font-bold mb-2">
                            {week.map((d, i) => (
                                <div key={i}>{d && d !== "out" ? `${new Date(d).getMonth() + 1}/${new Date(d).getDate()}（${weekdays[i]}）` : ""}</div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7 gap-2 text-xs">
                            {week.map((date, di) => (
                                <div key={di} className="space-y-2">
                                    {visiblePeriods.map(p => {
                                        if (!date || date === "out" || isHoliday(date, p)) {
                                            return <div key={p} className="border p-1 bg-gray-100 rounded text-gray-400" style={{ minHeight: '70px' }}>{p}講</div>;
                                        }
                                        const key = `${date}_${p}`;
                                        const data = scheduleMap[key] || {};
                                        const isDisabled = isStudentUnavailable(selectedStudent, date, p);

                                        const teacherSelected = !!data.teacherid;
                                        const subjectSelected = !!data.subjectid;
                                        const hadExistingData = !!data.previousTeacherid;

                                        let bgColor = "bg-white";

                                        if (teacherSelected && subjectSelected) {
                                            bgColor = "bg-blue-100"; // 保存対象（OK）
                                        } else if (hadExistingData && (!teacherSelected || !subjectSelected)) {
                                            bgColor = "bg-red-100"; // 既存データあり → 今は不完全 or 空 → 削除対象
                                        } else if (!hadExistingData && (teacherSelected || subjectSelected)) {
                                            bgColor = "bg-yellow-100"; // 新規で片方のみ → 警告対象
                                        }


                                        return (
                                            <div key={key} className={`border p-1 ${isDisabled ? "bg-gray-200" : bgColor} rounded shadow`} style={{ minHeight: '70px' }}>
                                                <div className="text-center font-semibold">{p}講</div>

                                                <select
                                                    value={data.teacherid || ""}
                                                    disabled={isDisabled}
                                                    className="w-full mb-1"
                                                    onChange={(e) => {
                                                        const newTeacherid = e.target.value;

                                                        setScheduleMap(prev => {
                                                            const current = prev[key] || {}; // ← 安全に取得

                                                            const isFirstEdit = current.teacherid !== newTeacherid;

                                                            return {
                                                                ...prev,
                                                                [key]: {
                                                                    ...current,
                                                                    teacherid: newTeacherid,
                                                                    previousTeacherid:
                                                                        current.hasOwnProperty("previousTeacherid")
                                                                            ? current.previousTeacherid
                                                                            : current.teacherid || undefined, // 初回のみセット、なければ undefined
                                                                }
                                                            };
                                                        });
                                                    }}

                                                >
                                                    <option value="">講師</option>
                                                    {teachers.map(t => {
                                                        const teacherKey = `${t.id}_${key}`;
                                                        const isUnavailable = isTeacherUnavailable(t.id, date, p);
                                                        const isFull = teacherSlotMap[teacherKey] >= 2;
                                                        return (
                                                            <option
                                                                key={t.id}
                                                                value={t.id}
                                                                disabled={isUnavailable || isFull}
                                                            >
                                                                {t.name}{isFull ? "（満席）" : ""}
                                                            </option>
                                                        );
                                                    })}
                                                </select>

                                                <select
                                                    value={data.subjectid || ""}
                                                    disabled={isDisabled}
                                                    className="w-full"
                                                    onChange={(e) => {
                                                        const newSubjectid = e.target.value;
                                                        setScheduleMap(prev => ({
                                                            ...prev,
                                                            [key]: {
                                                                ...prev[key],
                                                                subjectid: newSubjectid,
                                                            }
                                                        }));
                                                    }}
                                                >
                                                    <option value="">科目</option>
                                                    {subjects.map(sub => (
                                                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const getSubjectCounts = () => {
        const counts = {};
        Object.values(scheduleMap).forEach(val => {
            if (val.subjectid) {
                counts[val.subjectid] = (counts[val.subjectid] || 0) + 1;
            }
        });
        return counts;
    };

    // 固定部分
    const renderStickyBar = () => (
        <div
            style={{
                position: "sticky",
                top: 50,
                zIndex: 50,
                background: "#fff",
                borderBottom: "1px solid #e0e0e0",
                padding: "12px 16px",
                paddingLeft: "0px"
            }}
        >
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                <span className="font-bold mr-2">生徒スケジュール登録</span>

                {/* 学年選択 */}
                <select
                    value={selectedGrade}
                    onChange={e => setSelectedGrade(e.target.value)}
                    className="p-2 border rounded"
                    style={{ minWidth: 120 }}
                >
                    {gradeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>

                {/* 生徒選択 */}
                <select
                    className="p-2 border rounded"
                    value={selectedStudent || ""}
                    onChange={e => handleStudentSelect(e.target.value)}
                    style={{ minWidth: 150 }}
                >
                    <option value="">生徒を選択</option>
                    {filterStudentsFromGrade(students, selectedGrade).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                </select>

                {/* 科目別コマ数 */}
                {selectedStudent && (
                    <span className="text-sm text-gray-700" style={{ marginLeft: 8 }}>
                        <strong>コマ数:</strong>{" "}
                        {Object.entries(getSubjectCounts()).map(([subjectid, count]) => {
                            const subject = subjects.find(s => s.id === subjectid);
                            return (
                                <span key={subjectid} style={{ marginRight: 12 }}>
                                    {subject ? subject.name : subjectid}：{count}コマ
                                </span>
                            );
                        })}
                    </span>
                )}

                {entryWindow && (
                    <p >
                        対象期間：{new Date(entryWindow.start_date).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" })} 〜 {new Date(entryWindow.end_date).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" })}
                    </p>
                )}

                {/* 保存ボタン */}
                {selectedStudent && (
                    <button
                        onClick={handleSave}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        style={{ marginLeft: 12 }}
                    >
                        保存する
                    </button>
                )}
            </div>
        </div>
    );
    return (
        <div className="p-4" style={{ paddingTop: 0 }}>
            {renderStickyBar()}

            {selectedStudent && renderWeeklyCalendar()}

            {selectedStudent && (
                <div className="mt-6">
                    <button
                        onClick={handleSave}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                        保存する
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminStudentSchedule;
